import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// "Meekijken": the owner decides per person who reads along per module. This is
// about reading only — nothing here changes who may edit. No IT words on screen.
export const SettingsModuleAccess = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});   // { module: [hiddenUserId,...] }
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { removals: [{user,module}] }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/module-access");
      setState(data);
      setDraft(JSON.parse(JSON.stringify(data.hidden || {})));
    } catch (e) {
      toast.error(getErrorMessage(e, "Kan instellingen niet laden."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sees = (uid, moduleKey) => !(draft[moduleKey] || []).includes(uid);

  const toggle = (uid, moduleKey, willSee) => {
    setDraft((d) => {
      const cur = new Set(d[moduleKey] || []);
      if (willSee) cur.delete(uid); else cur.add(uid);
      return { ...d, [moduleKey]: Array.from(cur) };
    });
  };

  const modules = state?.modules || [];
  const users = state?.management_users || [];

  // Modules a user will see after saving (for the summary lines).
  const seenModules = (uid) => modules.filter((m) => sees(uid, m.key)).map((m) => m.label);

  // Removals compared to what is currently saved on the server.
  const removals = useMemo(() => {
    if (!state) return [];
    const out = [];
    modules.forEach((m) => {
      const before = new Set(state.hidden?.[m.key] || []);
      (draft[m.key] || []).forEach((uid) => {
        if (!before.has(uid)) {
          const u = users.find((x) => x.id === uid);
          out.push({ user: u?.name || uid, module: m.label });
        }
      });
    });
    return out;
  }, [draft, state, modules, users]);

  const doSave = async () => {
    setSaving(true);
    try {
      // Only send non-empty lists; strip empties.
      const hidden = {};
      Object.entries(draft).forEach(([k, v]) => { if (v && v.length) hidden[k] = v; });
      await api.put("/settings/module-access", { hidden });
      toast.success("Opgeslagen");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Opslaan mislukt."));
    } finally { setSaving(false); }
  };

  const onSaveClick = () => {
    if (removals.length > 0) setConfirm({ removals });
    else doSave();
  };

  const lastChange = state?.history?.[0];
  const fmtDate = (iso) => { try { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}`; } catch { return ""; } };

  if (loading) return <p className="text-body text-muted-foreground">Laden...</p>;

  return (
    <div className="space-y-4" data-testid="module-access">
      <Card className="p-5">
        <p className="max-w-3xl text-body text-muted-foreground">
          Jij, Joey en Timo zien altijd alles — TD-Ops is jullie werktool. Hieronder zet je per onderdeel wie er meekijkt.
          Dit gaat alleen over lezen. Wie iets mag wijzigen verandert hier niet.
        </p>
      </Card>

      {users.length === 0 ? (
        <Card className="p-6 text-center text-body text-muted-foreground">
          Er zijn nog geen meekijkers om in te stellen. Voeg ze toe onder Gebruikers.
        </Card>
      ) : (
        <>
          <Card className="p-5">
            <Table data-testid="module-access-table" wrapperClassName="hidden overflow-x-auto sm:block">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[240px]">Onderdeel</TableHead>
                    {users.map((u) => <TableHead key={u.id} className="text-center">{u.name}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modules.map((m) => (
                    <TableRow key={m.key} data-testid={`module-row-${m.key}`}>
                      <TableCell>
                        <div className="text-body font-medium text-foreground">{m.label}</div>
                        <div className="text-data text-muted-foreground">{m.explain}</div>
                      </TableCell>
                      {users.map((u) => (
                        <TableCell key={u.id} className="text-center">
                          <Checkbox
                            data-testid={`meekijk-${m.key}-${u.id}`}
                            checked={sees(u.id, m.key)}
                            onCheckedChange={(v) => toggle(u.id, m.key, !!v)}
                            aria-label={`${u.name} ziet ${m.label}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

            {/* Phone: one card per onderdeel, one line per meekijker. A matrix is
                not something you read sideways on a phone. */}
            <div className="space-y-3 sm:hidden" data-testid="module-access-cardlist">
              {modules.map((m) => (
                <div key={m.key} className="rounded border border-border p-3" data-testid={`module-card-${m.key}`}>
                  <div className="text-body font-medium text-foreground">{m.label}</div>
                  <div className="text-data text-muted-foreground">{m.explain}</div>
                  <div className="mt-2 space-y-2 border-t border-border pt-2">
                    {users.map((u) => (
                      <label key={u.id} className="flex min-h-11 items-center justify-between gap-3">
                        <span className="text-body text-foreground">{u.name}</span>
                        <Checkbox
                          data-testid={`meekijk-card-${m.key}-${u.id}`}
                          checked={sees(u.id, m.key)}
                          onCheckedChange={(v) => toggle(u.id, m.key, !!v)}
                          aria-label={`${u.name} ziet ${m.label}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-1 border-t border-border pt-3">
              {users.map((u) => {
                const seen = seenModules(u.id);
                const text = seen.length === modules.length ? "alles" : seen.length === 0 ? "niets van deze onderdelen" : seen.join(", ");
                return <p key={u.id} className="text-data text-muted-foreground" data-testid={`summary-${u.id}`}>{u.name} ziet straks: {text}</p>;
              })}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              {lastChange ? (
                <p className="text-data text-muted-foreground" data-testid="module-last-change">
                  Laatst gewijzigd: {fmtDate(lastChange.at)} door {lastChange.by_name} — {lastChange.module} {lastChange.to === "verborgen" ? "weggehaald bij" : "teruggezet voor"} {lastChange.user_name}.
                </p>
              ) : <span />}
              <Button data-testid="module-save-button" onClick={onSaveClick} disabled={saving}>Opslaan</Button>
            </div>
          </Card>

          <p className="max-w-3xl text-data text-muted-foreground">
            Dashboard, Locaties en Assets staan er niet bij — die lijsten gebruiken de andere schermen om namen op te zoeken.
            Instellingen kun je niet dichtzetten, anders zou je jezelf kunnen buitensluiten. Een nieuwe collega ziet in het begin alles.
          </p>
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Doorvoeren?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.removals.map((r, i) => (
                <span key={i} className="block">{r.user} ziet {r.module} straks niet meer.</span>
              ))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction data-testid="module-confirm-save" onClick={doSave} disabled={saving}>Doorvoeren</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
